import './assets/translations/config.ts';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { themeDark, themeLight } from '@resurs-internal/mui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { ApplicationHandler, CountryDialog, LoginCallback } from '@/components';
import { CountryProvider, KycSectionProvider } from '@/contexts';
import {
  ApplicationView,
  ChooseDepositAccountSection,
  DepositAccountViewV2,
  ErrorView,
  FixedTermView,
  FundsAddView,
  FundsView,
  HomeView,
  MaxAccountsView,
  NewApplicationView,
  PendingView,
  ResumeApplicationView,
  SummaryView,
  UnderageView,
} from '@/views';

import { ErrorBoundary } from './ErrorBoundary.tsx';
import { GTMWrapper } from './GTMWrapper.tsx';

const darkMode = localStorage.getItem('theme') === 'dark';

export const App = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    const title = t('title');

    if (import.meta.env.DEV) {
      document.title = `[${import.meta.env.MODE.toUpperCase()}] ${title}`;
    } else {
      document.title = title;
    }
  }, [t]);
  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    theme.palette.mode = darkMode ? 'dark' : 'light';
  }, [theme.palette]);

  return (
    <ThemeProvider theme={darkMode ? themeDark : themeLight}>
      <CssBaseline />

      <ErrorBoundary>
        <BrowserRouter>
          <CountryProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CountryDialog />
              <KycSectionProvider>
                <Routes>
                  <Route element={<GTMWrapper />}>
                    <Route element={<LoginCallback />} path="application">
                      <Route element={<NewApplicationView />} index />
                      <Route
                        element={<MaxAccountsView />}
                        path="max-accounts/:productCode"
                      />

                      <Route
                        element={<ApplicationHandler />}
                        path=":applicationId"
                      >
                        <Route element={<ApplicationView />} index />
                        <Route element={<SummaryView />} path="summary" />
                        <Route
                          element={<DepositAccountViewV2 />}
                          path="deposit-account"
                        />
                        <Route element={<FundsAddView />} path="add-funds" />
                        <Route element={<FundsView />} path="funds" />
                        <Route element={<PendingView />} path="pending" />
                      </Route>
                    </Route>

                    <Route element={<HomeView />} index />

                    <Route
                      element={<Navigate replace to="/" />}
                      path="/product"
                    />

                    <Route
                      element={<Navigate replace to="/" />}
                      path="/product/"
                    />

                    <Route
                      element={<HomeView />}
                      path="/product/:productCode"
                    />

                    <Route
                      element={<ChooseDepositAccountSection />}
                      path="/choose-account-type/:productCode?"
                    />

                    <Route
                      element={<FixedTermView />}
                      path="/choose-fixed-term/:productCode"
                    />

                    <Route
                      element={<LoginCallback />}
                      path="resume-application"
                    >
                      <Route element={<ErrorView code={'404'} />} index />
                      <Route
                        element={<ApplicationHandler />}
                        path=":applicationId"
                      >
                        <Route element={<ResumeApplicationView />} index />
                      </Route>
                    </Route>

                    <Route element={<ErrorView code={'500'} />} path="/error" />

                    <Route element={<ErrorView code={'404'} />} path="*" />

                    <Route
                      element={<ErrorView code={'denied'} />}
                      path="denied"
                    />
                    <Route element={<UnderageView />} path="underage" />
                  </Route>
                </Routes>
              </KycSectionProvider>
            </LocalizationProvider>
          </CountryProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
